<template>
  <modal-wrap :name="modalName" :header-class="'user-header'" body-class="user-modal" :hide-footer="true" size="lg">
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ title }}</h4>
      </div>
    </template>
    <template #default>
      <VeeForm @submit="handleSubmit">
        <div class="mt-2">
          <b-alert v-if="mode === 'create'" :model-value="true" variant="info" class="mb-3">
            No company is required. After creation, set a password using Edit or ask the user to use Forgot Password.
          </b-alert>
          <div class="form-group">
            <Field
              v-slot="{ field, errors, meta }"
              :rules="{ required: true, email: true }"
              :model-value="form.email"
              name="email"
            >
              <label for="email">Email *</label>
              <input
                v-bind="field"
                v-model="form.email"
                type="email"
                :class="['form-control', getValidationState(meta)]"
              />
              <span class="text-danger">{{ errors.join() }}</span>
            </Field>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="form.firstName"
                  name="firstName"
                  :rules="nameRules"
                >
                  <label for="firstName">First name *</label>
                  <input
                    v-bind="field"
                    v-model="form.firstName"
                    type="text"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="form.lastName"
                  name="lastName"
                  :rules="nameRules"
                >
                  <label for="lastName">Last name *</label>
                  <input
                    v-bind="field"
                    v-model="form.lastName"
                    type="text"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </div>

          <div v-if="mode === 'edit'" class="row">
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="password"
                  :rules="{ userPassword: true, required: false }"
                  name="password"
                >
                  <label for="password">Password</label>
                  <input
                    v-bind="field"
                    v-model="password"
                    type="password"
                    autocomplete="off"
                    placeholder="Password"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="confirmedPassword"
                  :rules="{ confirmed: '@password', userPassword: true, required: false }"
                  name="confirmedPassword"
                >
                  <label for="confirmedPassword">Confirm Password</label>
                  <input
                    v-bind="field"
                    v-model="confirmedPassword"
                    type="password"
                    autocomplete="off"
                    placeholder="Confirm Password"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </div>
          <div v-if="mode === 'edit'" class="text-muted mb-2">
            New password is set only if provided and both fields match.
          </div>

          <div class="form-check mb-3">
            <input
              id="isInventoryAvailable"
              v-model="form.isInventoryAvailable"
              type="checkbox"
              class="form-check-input"
            />
            <label class="form-check-label" for="isInventoryAvailable">Inventory Management access</label>
          </div>

          <div class="sender-footer mt-3">
            <div class="w-100 m-0">
              <b-button type="submit" variant="success" class="w-100" :disabled="submitting">
                {{ actionLabel }}
              </b-button>
            </div>
          </div>
        </div>
      </VeeForm>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { CreateStaffUserRequest } from '@/types/Api/StaffUser'
import type { StaffUserForm } from '@/types/Components/StaffUserForm'
import type { StaffUser } from '@/types/Models/StaffUser'
import { BAlert } from 'bootstrap-vue-next'
import { Form as VeeForm, Field } from 'vee-validate'
import { computed, defineComponent, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue'
import { STAFF } from '@/const/AccountTypes'
import { getValidationState } from '@/services/Validation'
import { useStaffUsersStore } from '@/store/staffUsers'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'StaffUserModal',
  components: {
    BAlert,
    ModalWrap,
    VeeForm,
    Field,
  },
  props: {
    modalName: {
      type: String,
      required: true,
    },
    mode: {
      type: String as () => 'create' | 'edit',
      required: true,
    },
  },
  emits: ['saved'],
  setup(props, { emit }) {
    const modalStore = useVueNextModalStore()
    const staffUsersStore = useStaffUsersStore()
    const submitting: Ref<boolean> = ref(false)
    const form: Ref<StaffUserForm> = ref({
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      isInventoryAvailable: true,
    })
    const password: Ref<string> = ref('')
    const confirmedPassword: Ref<string> = ref('')
    const nameRules = { required: true, min: 2 }
    const modalData: ComputedRef<{ user?: StaffUser }> = computed(() => modalStore.modalPropsByName(props.modalName) || {})
    const title: ComputedRef<string> = computed(() => (props.mode === 'create' ? 'Create staff user' : 'Edit staff user'))
    const actionLabel: ComputedRef<string> = computed(() => (props.mode === 'create' ? 'Create' : 'Save'))

    const closeModal = (): void => {
      modalStore.closeModal(props.modalName)
    }

    const resetForm = (): void => {
      form.value = {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        isInventoryAvailable: true,
      }
      password.value = ''
      confirmedPassword.value = ''
    }

    const setFormFromUser = (user?: StaffUser): void => {
      form.value = {
        id: user?.id || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        isInventoryAvailable: user?.isInventoryAvailable ?? true,
      }
      password.value = ''
      confirmedPassword.value = ''
    }

    const handleSubmit = (): Promise<void> | void => {
      submitting.value = true
      if (props.mode === 'create') {
        const payload: CreateStaffUserRequest = {
          type: STAFF,
          firstName: form.value.firstName,
          lastName: form.value.lastName,
          email: form.value.email,
          isInventoryAvailable: form.value.isInventoryAvailable,
        }

        return staffUsersStore
          .createStaffUser(payload)
          .then((created) => {
            if (created !== false) {
              resetForm()
              closeModal()
              emit('saved')
            }
          })
          .finally(() => {
            submitting.value = false
          })
      }

      if (!form.value.id) {
        submitting.value = false
        return
      }

      return staffUsersStore
        .updateStaffUser({
          id: form.value.id,
          data: {
            firstName: form.value.firstName,
            lastName: form.value.lastName,
            email: form.value.email,
            isInventoryAvailable: form.value.isInventoryAvailable,
            ...(password.value ? { password: password.value } : {}),
          },
        })
        .then((saved) => {
          if (saved !== false) {
            closeModal()
            emit('saved')
          }
        })
        .finally(() => {
          submitting.value = false
        })
    }

    onMounted(() => {
      if (props.mode === 'edit') {
        setFormFromUser(modalData.value?.user)
      }
    })

    watch(
      () => modalData.value.user,
      (user) => {
        if (props.mode === 'edit') {
          setFormFromUser(user)
        }
      },
    )

    return {
      title,
      actionLabel,
      form,
      password,
      confirmedPassword,
      nameRules,
      submitting,
      handleSubmit,
      getValidationState,
    }
  },
})
</script>
